'use client';

import Icon from '@ant-design/icons';
import { message } from 'antd';
import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';

import DoneIcon from '@/components/Icon/svgs/check_circle.svg';
import RightSvg from '@/components/Icon/svgs/right.svg';
import UndoneIcon from '@/components/Icon/svgs/todo_undone.svg';
import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import { useTranslation } from '@/i18n/client';
import type { ICircleSettingDetail, IGroupUserInfo } from '@/service/friend';
import { EOperationType, circleAdminManage, getCircleMemberList } from '@/service/friend';

import styles from './index.less';

interface IProps {
  mode: 'add' | 'remove';
  circleRoute: string;
  circleDetail?: ICircleSettingDetail | null;
  maxAddCount: number;
  isAnimating: boolean;
  animationDirection: 'forward' | 'backward';
  onBack: () => void;
  onSuccess: () => void;
}

export const AdminSelectView = (props: IProps): JSX.Element => {
  const { mode, circleRoute, maxAddCount, isAnimating, animationDirection, onBack, onSuccess } =
    props;
  const { t } = useTranslation();

  const [memberList, setMemberList] = useState<IGroupUserInfo[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const isAddMode = mode === 'add';

  // 获取成员列表
  // TODO：过滤管理员的逻辑不对，不能直接用circleDetail?.members去过滤
  const fetchMemberList = useCallback(() => {
    setLoading(true);

    getCircleMemberList({
      route: circleRoute,
      adminOnly: !isAddMode
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        const responseData = res.data.data.list.filter((item) => item.role !== 'OWNER');

        setMemberList(responseData);
      })
      .catch((e) => {
        message.error(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [circleRoute, isAddMode, t]);

  useEffect(() => {
    fetchMemberList();
  }, [fetchMemberList]);

  // 选择成员
  const handleSelectMember = (member: IGroupUserInfo) => {
    setSelectedIds((prev) => {
      const isSelected = prev.includes(member.id);

      if (isSelected) {
        return prev.filter((id) => id !== member.id);
      }

      // Add 模式下检查最大数量限制
      if (isAddMode && prev.length >= maxAddCount) {
        message.warning(t('circle.maxAdminReached', { count: maxAddCount }));

        return prev;
      }

      return [...prev, member.id];
    });
  };

  // 提交
  const handleSubmit = () => {
    if (selectedIds.length === 0) {
      message.warning(t('friend.selectMemberTip'));

      return;
    }

    setSubmitLoading(true);

    circleAdminManage({
      route: circleRoute,
      operationType: isAddMode ? EOperationType.ADD : EOperationType.REMOVE,
      userIds: selectedIds
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        message.success(t(isAddMode ? 'circle.addAdminSuccess' : 'circle.removeAdminSuccess'));
        onSuccess();
      })
      .catch((e: Error) => {
        message.error(e.message);
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const titleKey = isAddMode ? 'circle.addAdmin' : 'circle.removeAdmin';
  const confirmTextKey = isAddMode ? 'circle.add' : 'circle.remove';

  return (
    <div
      className={classNames(styles.selectContainer, {
        [styles.slideIn]: !isAnimating && animationDirection === 'forward',
        [styles.slideOut]: isAnimating && animationDirection === 'backward'
      })}
    >
      {/* 头部 */}
      <div className={styles.selectHeader}>
        <div className={styles.backBtn} onClick={onBack}>
          <Icon className={styles.backIcon} component={RightSvg} />
        </div>
        <div className={styles.title}>{t(titleKey)}</div>
        <div className={styles.placeholder} />
      </div>

      {/* 成员列表 */}
      <div className={styles.memberListContainer}>
        <div className={styles.memberListTitle}>
          {isAddMode ? t('circle.selectMembersToAdd') : t('circle.selectAdminsToRemove')}
          {isAddMode && maxAddCount > 0 && (
            <span className={styles.countHint}>
              ({selectedIds.length}/{maxAddCount})
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center items-center w-full py-20">
            <LottieJumpLoading className="!w-16 !h-16" />
          </div>
        ) : memberList.length > 0 ? (
          memberList.map((item) => {
            const isSelected = selectedIds.includes(item.id);
            const isAdmin = ['OWNER', 'ADMIN'].includes(item.role);

            return (
              <div
                key={item.id}
                className={classNames(styles.memberSelectItem, {
                  [styles.active]: isSelected,
                  [styles.disabled]: isAddMode && isAdmin
                })}
                onClick={() => handleSelectMember(item)}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img alt={item.username} className={styles.avatar} src={item.avatar} />
                <div className={styles.memberInfo}>
                  <div className={styles.memberUsername}>{item.nickname || item.username}</div>
                </div>
                <div className={styles.iconWrapper}>
                  {isSelected || (isAddMode && isAdmin) ? (
                    <Icon className={styles.icon} component={DoneIcon} />
                  ) : (
                    <Icon
                      className={classNames(styles.icon, styles.undone)}
                      component={UndoneIcon}
                    />
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className={styles.emptyText}>
            {isAddMode ? t('circle.noMembersToAdd') : t('circle.noAdminsToRemove')}
          </div>
        )}
      </div>

      {/* 确认按钮 */}
      <div
        className={classNames(styles.confirmBtn, {
          [styles.disabled]: submitLoading || selectedIds.length === 0
        })}
        onClick={() => {
          if (submitLoading || selectedIds.length === 0) return;

          handleSubmit();
        }}
      >
        {submitLoading
          ? isAddMode
            ? t('common.adding')
            : t('common.removing')
          : t(confirmTextKey)}
      </div>
    </div>
  );
};

export default AdminSelectView;
